import type { Device, Room, Area } from '../types';

/**
 * Sorts devices by their hierarchical order: area → room → device
 *
 * @param devices - Array of devices to sort
 * @param rooms - Array of rooms (needed for room sorting)
 * @param areas - Array of areas (needed for area sorting)
 * @returns Sorted array of devices
 */
export function sortDevicesByHierarchy(devices: Device[], rooms: Room[], areas: Area[]): Device[] {
  // Create lookup maps for efficient access
  const roomMap = new Map(rooms.map((r) => [r.id, r]));
  const areaMap = new Map(areas.map((a) => [a.id, a]));

  // Sort devices
  return [...devices].sort((a, b) => {
    // Get room information
    const roomA = a.roomId ? roomMap.get(a.roomId) : null;
    const roomB = b.roomId ? roomMap.get(b.roomId) : null;

    // Get area information
    const areaA = roomA?.areaId ? areaMap.get(roomA.areaId) : null;
    const areaB = roomB?.areaId ? areaMap.get(roomB.areaId) : null;

    // 1. Sort by area sortOrder (unassigned areas go last)
    const areaOrderA = areaA?.sortOrder ?? 999999;
    const areaOrderB = areaB?.sortOrder ?? 999999;
    if (areaOrderA !== areaOrderB) {
      return areaOrderA - areaOrderB;
    }

    // 2. Within same area, sort by room sortOrder (unassigned rooms go last)
    const roomOrderA = roomA?.sortOrder ?? 999999;
    const roomOrderB = roomB?.sortOrder ?? 999999;
    if (roomOrderA !== roomOrderB) {
      return roomOrderA - roomOrderB;
    }

    // 3. Within same room, sort by device sortOrder
    const deviceOrderA = a.sortOrder ?? 999999;
    const deviceOrderB = b.sortOrder ?? 999999;
    if (deviceOrderA !== deviceOrderB) {
      return deviceOrderA - deviceOrderB;
    }

    // 4. Final fallback: alphabetical by device name
    return a.deviceName.localeCompare(b.deviceName);
  });
}

/**
 * Sorts rooms by their hierarchical order: area → room
 *
 * @param rooms - Array of rooms to sort
 * @param areas - Array of areas (needed for area sorting)
 * @returns Sorted array of rooms
 */
export function sortRoomsByHierarchy(rooms: Room[], areas: Area[]): Room[] {
  const areaMap = new Map(areas.map((a) => [a.id, a]));

  return [...rooms].sort((a, b) => {
    const areaA = a.areaId ? areaMap.get(a.areaId) : null;
    const areaB = b.areaId ? areaMap.get(b.areaId) : null;

    // Sort by area sortOrder
    const areaOrderA = areaA?.sortOrder ?? 999999;
    const areaOrderB = areaB?.sortOrder ?? 999999;
    if (areaOrderA !== areaOrderB) {
      return areaOrderA - areaOrderB;
    }

    // Within same area, sort by room sortOrder
    if (a.sortOrder !== b.sortOrder) {
      return a.sortOrder - b.sortOrder;
    }

    // Fallback: alphabetical by room name
    return a.roomName.localeCompare(b.roomName);
  });
}

/**
 * Sorts areas by their sortOrder
 *
 * @param areas - Array of areas to sort
 * @returns Sorted array of areas
 */
export function sortAreas(areas: Area[]): Area[] {
  return [...areas].sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) {
      return a.sortOrder - b.sortOrder;
    }
    return a.areaName.localeCompare(b.areaName);
  });
}

/**
 * Predefined order for device control types
 * Used to sort device types in a consistent, user-friendly order
 */
const DEVICE_TYPE_ORDER = [
  'light',
  'switch',
  'sensor',
  'cover',
  'thermostat',
  'camera',
  'lock',
  'fan',
  'outlet',
  'climate',
  'binary_sensor',
  'media_player',
  'alarm',
];

/**
 * Compares two device types for sorting
 * Uses predefined order, alphabetical fallback
 *
 * @param typeA - First device type
 * @param typeB - Second device type
 * @returns Sort comparison result
 */
export function compareDeviceTypes(typeA: string, typeB: string): number {
  const indexA = DEVICE_TYPE_ORDER.indexOf(typeA.toLowerCase());
  const indexB = DEVICE_TYPE_ORDER.indexOf(typeB.toLowerCase());

  // If both types are in the predefined order, use that order
  if (indexA !== -1 && indexB !== -1) {
    return indexA - indexB;
  }

  // If only one is in the predefined order, it comes first
  if (indexA !== -1) return -1;
  if (indexB !== -1) return 1;

  // If neither is in the predefined order (like 'no-type'), sort alphabetically
  // 'no-type' should go last
  if (typeA === 'no-type') return 1;
  if (typeB === 'no-type') return -1;

  return typeA.localeCompare(typeB);
}
